import { useCallback, useRef } from 'react'
import { useFileBrowserStore } from '../stores/fileBrowserStore'
import { FileListRow } from './FileListRow'
import { fileName } from '@/shared/api/generated'
import type { S3Object } from '@/shared/api/generated'

const PermDelete = 1
const PermDownload = 4
const PermRename = 16

interface FileListProps {
  onNavigate: (obj: S3Object) => void
  onDownload: (obj: S3Object) => void
  onPreview: (obj: S3Object) => void
  onRename: (obj: S3Object, newName: string) => void
  onContextMenu: (e: React.MouseEvent, obj: S3Object) => void
  renamingKey: string | null
  onRenameCancel: () => void
  onDragStart: (e: React.DragEvent, obj: S3Object) => void
  dragEnabled: boolean
}

export function FileList({ onNavigate, onDownload, onPreview, onRename, onContextMenu, renamingKey, onRenameCancel, onDragStart, dragEnabled }: FileListProps) {
  const { files, filesLoading, hasMore, loadMore, currentPath, currentGroupPerms, selectedIndex, setSelectedIndex, checkedItems, toggleCheck, sortColumn, sortDirection, setSort, rootGroups } = useFileBrowserStore()

  const canDelete = (currentGroupPerms & PermDelete) !== 0
  const canDownload = (currentGroupPerms & PermDownload) !== 0
  const canDrag = dragEnabled && (currentGroupPerms & PermRename) !== 0
  const isRoot = currentPath === '/'

  const nameOf = new Map<string, string>()
  const timeOf = new Map<string, number>()
  for (const f of files) {
    nameOf.set(f.key, fileName(f))
    timeOf.set(f.key, f.last_modified ? new Date(f.last_modified).getTime() : 0)
  }

  const sorted = [...files].sort((a, b) => {
    if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1
    const dir = sortDirection === 'asc' ? 1 : -1
    let cmp = 0
    switch (sortColumn) {
      case 'name':
        cmp = nameOf.get(a.key)!.localeCompare(nameOf.get(b.key)!, undefined, { sensitivity: 'base' })
        break
      case 'size':
        cmp = a.size - b.size
        break
      case 'modified':
        cmp = timeOf.get(a.key)! - timeOf.get(b.key)!
        break
    }
    return cmp !== 0 ? cmp * dir : nameOf.get(a.key)!.localeCompare(nameOf.get(b.key)!, undefined, { sensitivity: 'base' })
  })

  const sortedRef = useRef(sorted)
  sortedRef.current = sorted

  const handleClick = useCallback((i: number) => setSelectedIndex(i), [setSelectedIndex])
  const handleDoubleClick = useCallback((i: number) => {
    const obj = sortedRef.current[i]
    if (!obj) return
    if (obj.is_dir) onNavigate(obj)
    else onPreview(obj)
  }, [onNavigate, onPreview])
  const handleCheck = useCallback((i: number) => {
    const obj = sortedRef.current[i]
    if (obj) toggleCheck(obj)
  }, [toggleCheck])
  const handleContextMenu = useCallback((e: React.MouseEvent, i: number) => {
    const obj = sortedRef.current[i]
    if (obj) onContextMenu(e, obj)
  }, [onContextMenu])
  const handleDownload = useCallback((i: number) => {
    const obj = sortedRef.current[i]
    if (obj) onDownload(obj)
  }, [onDownload])
  const handleDragStart = useCallback((e: React.DragEvent, i: number) => {
    const obj = sortedRef.current[i]
    if (obj) onDragStart(e, obj)
  }, [onDragStart])
  const handleRename = useCallback((i: number, newName: string) => {
    const obj = sortedRef.current[i]
    if (obj) onRename(obj, newName)
  }, [onRename])

  const allChecked = sorted.length > 0 && sorted.every(f => checkedItems.has(f.key))
  const someChecked = sorted.some(f => checkedItems.has(f.key))

  const toggleAll = () => {
    const store = useFileBrowserStore.getState()
    if (allChecked) {
      store.clearChecked()
    } else {
      for (const obj of sorted) store.toggleCheck(obj)
      const next = new Map(store.checkedItems)
      for (const obj of sorted) next.set(obj.key, obj)
      useFileBrowserStore.setState({ checkedItems: next })
    }
  }

  if (filesLoading) {
    return <div className="empty-state" data-testid="file-list-loading">Loading…</div>
  }

  if (isRoot && sorted.length === 0) {
    return <div className="empty-state" data-testid="file-list">No groups available</div>
  }

  if (sorted.length === 0) {
    return <div className="empty-state" data-testid="file-list">Empty folder</div>
  }

  return (
    <div className="file-list" data-testid="file-list">
      <table>
        <thead>
          <tr>
            <th className="col-check">
              {!isRoot && (
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked }}
                  onChange={toggleAll}
                  data-testid="select-all"
                />
              )}
            </th>
            <th className="col-badge"></th>
            <SortHeader column="name" label="Name" current={sortColumn} direction={sortDirection} onSort={setSort} />
            <SortHeader column="size" label="Size" current={sortColumn} direction={sortDirection} onSort={setSort} />
            <SortHeader column="modified" label="Modified" current={sortColumn} direction={sortDirection} onSort={setSort} />
            <th className="col-actions" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((obj, i) => (
            <FileListRow
              key={obj.key}
              obj={obj}
              index={i}
              checked={checkedItems.has(obj.key)}
              selected={i === selectedIndex}
              renaming={renamingKey === obj.key}
              draggable={!isRoot && canDrag}
              onCheck={handleCheck}
              onClick={handleClick}
              onDoubleClick={handleDoubleClick}
              onContextMenu={handleContextMenu}
              onDownload={handleDownload}
              onRename={handleRename}
              onRenameCancel={onRenameCancel}
              canDownload={isRoot ? ((rootGroups.get(obj.key)?.perms ?? 0) & PermDownload) !== 0 : canDownload}
              canDelete={isRoot ? false : canDelete}
              onDragStart={handleDragStart}
              registryEnabled={isRoot ? rootGroups.get(obj.key)?.registry_enabled : undefined}
              iconOverride={isRoot ? (rootGroups.get(obj.key)?.view_id
                ? (rootGroups.get(obj.key)?.base_path?.startsWith('//') ? '🗄️' : '🔗')
                : (rootGroups.get(obj.key)?.role === 'admin' ? '👑' : undefined))
                : undefined}
            />
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="load-more-wrap">
          <button className="load-more" onClick={loadMore}>Load more</button>
        </div>
      )}
    </div>
  )
}

type SortColumn = 'name' | 'size' | 'modified'

function SortHeader({ column, label, current, direction, onSort }: {
  column: SortColumn; label: string; current: SortColumn; direction: 'asc' | 'desc'
  onSort: (c: SortColumn) => void
}) {
  const active = current === column
  const arrow = active ? (direction === 'asc' ? ' ▲' : ' ▼') : ''
  return (
    <th className={`col-${column} sortable`} onClick={() => onSort(column)}>
      {label}{arrow}
    </th>
  )
}
